/**
 * Step00_Regulation - 실시규정 화면
 * 첨부 이미지 기준 UI(기본정보 + 평가척도 + 위험성 수준 테이블)
 */
import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  Select,
  MenuItem,
  TextField,
  Divider,
} from '@mui/material';
import { Unstable_Grid2 as Grid } from '@mui/material';
import { MultiChipSelect } from '../../../../../Components/MUIs';

const RS_GUBUN_ITEMS = ['최초평가', '정기평가', '수시평가'];
const RS_METHOD_ITEMS = ['빈도·강도법', '위험성 수준 3단계판단법'];

const ACTION_ITEMS = ['현재상태유지', '개선', '즉시개선'];

/** 위험성 수준 min/max 점수 상한 */
const LEVEL_SCORE_MAX = 20;

const ClampLevelScore = (raw) => {
  if (raw === '' || raw == null) return 0;
  const p = parseInt(String(raw).replace(/[^\d]/g, ''), 10);
  if (Number.isNaN(p)) return 0;
  return Math.min(LEVEL_SCORE_MAX, Math.max(0, p));
};

/** 평가척도 라디오 — 기획 이미지와 동일 파란 계열 */
const SCALE_RADIO_UI = {
  selectedBg: '#E8F5E9',
  selectedBorder: '#A5D6A7',
  dotBlue: '#2CA900',
  ringGrey: '#B0BEC5',
};

const REGULATION_UI = {
  pageBg: '#f3f6fb',
  cardBorder: 'rgba(15, 23, 42, 0.08)',
  cardShadow: '0 10px 24px rgba(15, 23, 42, 0.08)',
  cardShadowHover: '0 14px 28px rgba(15, 23, 42, 0.11)',
  sectionTitle: '#1f2937',
  sectionSubTitle: '#475467',
};

const scaleRadioControlSx = {
  p: 0.5,
  color: SCALE_RADIO_UI.ringGrey,
  '&.Mui-checked': { color: SCALE_RADIO_UI.dotBlue },
};

const getScaleOptionLabelSx = (selected) => ({
  m: 0,
  px: 1.5,
  py: 0.875,
  borderRadius: '6px',
  border: '1px solid',
  borderColor: selected ? SCALE_RADIO_UI.selectedBorder : 'transparent',
  bgcolor: selected ? SCALE_RADIO_UI.selectedBg : 'transparent',
  transition: 'background-color 0.18s ease, border-color 0.18s ease',
  '& .MuiFormControlLabel-label': {
    fontSize: '0.9375rem',
    fontWeight: selected ? 600 : 500,
    color: 'rgba(0, 0, 0, 0.88)',
  },
});

export const DEFAULT_LEVELS_3X3 = [
  { label: '낮음', min: 1, max: 3, action: '현재상태유지', tone: 'green' },
  { label: '보통', min: 4, max: 6, action: '개선', tone: 'yellow' },
  { label: '높음', min: 7, max: 9, action: '즉시개선', tone: 'red' },
];

export const DEFAULT_LEVELS_5X4 = [
  { label: '매우 낮음', min: 1, max: 2, action: '현재상태유지', tone: 'green' },
  { label: '낮음', min: 3, max: 5, action: '현재상태유지', tone: 'lime' },
  { label: '보통', min: 6, max: 8, action: '개선', tone: 'yellow' },
  { label: '약간 높음', min: 9, max: 10, action: '개선', tone: 'peach' },
  { label: '높음', min: 11, max: 15, action: '즉시개선', tone: 'orange' },
  { label: '매우높음', min: 16, max: 20, action: '즉시개선', tone: 'red' },
];

const toneStyle = (tone) => {
  switch (tone) {
    case 'green':
      return { chip: '#66E0B3', bg: '#E9FBF3', border: '#C8F3E2' };
    case 'lime':
      return { chip: '#B7F05D', bg: '#F2FCE3', border: '#E3F7B9' };
    case 'yellow':
      return { chip: '#FFE44D', bg: '#FFFBE0', border: '#FFF2A8' };
    case 'peach':
      return { chip: '#FFBF7A', bg: '#FFF1E5', border: '#FFE0C2' };
    case 'orange':
      return { chip: '#FF9F40', bg: '#FFECDC', border: '#FFD3B0' };
    case 'red':
      return { chip: '#FF6B6B', bg: '#FFE8E8', border: '#FFCACA' };
    default:
      return { chip: '#E0E0E0', bg: '#FAFAFA', border: '#EEEEEE' };
  }
};

function Regulation({ value, onChange, onCancel, onSave, deptItems }) {
  const rsNm = value?.rsNm ?? '';
  const rsGubun = value?.rsGubun ?? '최초평가';
  const rsMethod = value?.rsMethod ?? '빈도·강도법';
  const depNm = value?.depNm ?? [];
  const startDate = value?.startDate ?? '';
  const endDate = value?.endDate ?? '';
  const scale = value?.scale ?? '3x3';
  const levels =
    value?.levels ??
    (scale === '5x4' ? DEFAULT_LEVELS_5X4 : DEFAULT_LEVELS_3X3);

  const isFrequency = rsMethod === '빈도·강도법';

  // 부서는 RegulationF에서 GetDepLast 조회 후 deptItems로 전달됨 → 조회 결과만 사용
  const departmentOptions = useMemo(() => {
    const raw = Array.isArray(deptItems) ? deptItems : [];
    return raw
      .map((it) => {
        if (it == null) return null;
        if (typeof it === 'string') return { label: it, value: it };
        const val = it.value ?? it.depCd ?? it.id ?? it.code ?? it.name ?? '';
        const label = it.label ?? it.depNm ?? it.text ?? it.name ?? String(val);
        if (!val) return null;
        return { label, value: val };
      })
      .filter(Boolean);
  }, [deptItems]);

  const handleLevelChange = (idx, key, value) => {
    const next = [...levels];
    next[idx] = { ...next[idx], [key]: value };
    onChange?.({ levels: next });
  };

  const handleLevelScoreInput = (idx, key, raw) => {
    handleLevelChange(idx, key, ClampLevelScore(raw));
  };

  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      bgcolor: '#fff',
      borderRadius: 1.75,
      minHeight: 38,
      transition: 'all 0.18s ease',
      '& fieldset': {
        borderColor: '#d0d5dd',
      },
      '&:hover fieldset': {
        borderColor: '#98a2b3',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#2CA900',
        boxShadow: '0 0 0 3px rgba(44,169,0,0.14)',
      },
    },
    '& .MuiInputLabel-root': {
      fontWeight: 700,
    },
  };

  const Caption = ({ text, required }) => (
    <Typography
      variant="caption"
      sx={{
        fontWeight: 800,
        color: 'text.primary',
        fontSize: { xs: '0.88rem', sm: '0.96rem' },
        lineHeight: 1.35,
      }}
    >
      {text} {required ? <span style={{ color: '#d32f2f' }}>*</span> : null}
    </Typography>
  );

  const cardSx = {
    borderRadius: 2.25,
    width: '100%',
    bgcolor: '#fff',
    borderColor: REGULATION_UI.cardBorder,
    boxShadow: REGULATION_UI.cardShadow,
    transition: 'box-shadow 0.2s ease, transform 0.2s ease',
    '&:hover': {
      boxShadow: REGULATION_UI.cardShadowHover,
      transform: 'translateY(-1px)',
    },
  };

  return (
    <Box
      sx={{
        p: { xs: 1.5, sm: 2 },
        width: '100%',
        bgcolor: REGULATION_UI.pageBg,
        borderRadius: 2.5,
      }}
    >
      <Box
        sx={{
          mb: 1.75,
          p: { xs: 1.4, sm: 1.8 },
          borderRadius: 2,
          border: '1px solid rgba(44,169,0,0.2)',
          background:
            'linear-gradient(90deg, rgba(232,245,233,0.9) 0%, rgba(244,250,245,0.98) 100%)',
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 900,
            letterSpacing: -0.2,
            color: REGULATION_UI.sectionTitle,
          }}
        >
          위험성평가 기본정보
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: REGULATION_UI.sectionSubTitle, mt: 0.25 }}
        >
          기본 정보를 입력하고, 빈도·강도법 선택 시 평가척도를 설정하세요.
        </Typography>
      </Box>

      <Paper
        variant="outlined"
        sx={{
          ...cardSx,
          p: 1.5,
          mb: 1.25,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 900,
            mb: 1.5,
            color: REGULATION_UI.sectionTitle,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.6,
            '&::before': {
              content: '""',
              width: 4,
              height: 16,
              borderRadius: 999,
              bgcolor: '#2CA900',
              display: 'inline-block',
            },
          }}
        >
          기본 정보
        </Typography>
        <Grid container spacing={2} columns={{ xs: 4, sm: 8, md: 12 }}>
          <Grid xs={4} md={6}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Caption text="위험성평가명" required />
              <TextField
                fullWidth
                size="small"
                placeholder="위험성평가명을 입력하세요"
                value={rsNm}
                onChange={(e) => onChange?.({ rsNm: e.target.value })}
                required
                sx={fieldSx}
              />
            </Box>
          </Grid>
          <Grid xs={4} md={3}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Caption text="평가구분" required />
              <FormControl fullWidth size="small" required sx={fieldSx}>
                <Select
                  value={rsGubun}
                  onChange={(e) => onChange?.({ rsGubun: e.target.value })}
                >
                  {RS_GUBUN_ITEMS.map((v) => (
                    <MenuItem key={v} value={v}>
                      {v}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Grid>
          <Grid xs={4} md={3}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Caption text="평가방법" required />
              <FormControl fullWidth size="small" required sx={fieldSx}>
                <Select
                  value={rsMethod}
                  onChange={(e) => onChange?.({ rsMethod: e.target.value })}
                >
                  {RS_METHOD_ITEMS.map((v) => (
                    <MenuItem key={v} value={v}>
                      {v}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Grid>

          <Grid xs={4} md={9}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Caption text="평가부서" required />
              <MultiChipSelect
                items={departmentOptions}
                value={Array.isArray(depNm) ? depNm : []}
                onChange={(e) => {
                  const value =
                    typeof e.target.value === 'string'
                      ? e.target.value.split(',')
                      : e.target.value;
                  const clean = (value || []).filter(Boolean);
                  onChange?.({ depNm: clean });
                }}
                onClose={() => {
                  // 빈 값 제거(모달 방식과 동일한 정리)
                  onChange?.({
                    depNm: Array.isArray(depNm) ? depNm.filter(Boolean) : [],
                  });
                }}
                labelYn={false}
                sx={fieldSx}
              />
            </Box>
          </Grid>

          <Grid xs={4} md={3}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Caption text="평가기간" required />
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <TextField
                  size="small"
                  type="date"
                  value={startDate}
                  onChange={(e) => onChange?.({ startDate: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  required
                  sx={{ ...fieldSx, flex: 1, minWidth: 0 }}
                />
                <Typography sx={{ color: 'text.secondary', flexShrink: 0 }}>
                  -
                </Typography>
                <TextField
                  size="small"
                  type="date"
                  value={endDate}
                  onChange={(e) => onChange?.({ endDate: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  required
                  sx={{ ...fieldSx, flex: 1, minWidth: 0 }}
                />
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <Paper
        variant="outlined"
        sx={{
          ...cardSx,
          p: 1.5,
        }}
      >
        {isFrequency ? (
          <>
            <Box sx={{ mb: 1.25 }}>
              <Caption text="평가척도" required />
            </Box>
            <RadioGroup
              row
              value={scale}
              onChange={(e) => onChange?.({ scale: e.target.value })}
              sx={{
                mb: 1.25,
                gap: 1.25,
                flexWrap: 'wrap',
                alignItems: 'center',
                p: 0.5,
                borderRadius: 1.5,
                bgcolor: '#f8fbff',
                border: '1px solid rgba(44,169,0,0.18)',
              }}
            >
              {['3x3', '5x4'].map((opt) => (
                <FormControlLabel
                  key={opt}
                  value={opt}
                  control={
                    <Radio
                      disableRipple
                      size="small"
                      sx={scaleRadioControlSx}
                    />
                  }
                  label={opt}
                  sx={getScaleOptionLabelSx(scale === opt)}
                />
              ))}
            </RadioGroup>

            <Divider sx={{ mb: 1.0 }} />

            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 900,
                mb: 1,
                fontSize: { xs: '0.88rem', sm: '0.96rem' },
                lineHeight: 1.35,
              }}
            >
              위험성 수준
            </Typography>

            <Paper
              variant="outlined"
              sx={{
                p: 1.1,
                borderRadius: 2,
                bgcolor: '#f8fafc',
                borderColor: 'rgba(2, 6, 23, 0.1)',
                // 페이지 스크롤을 줄이기 위해 화면 높이에 맞춰 제한 (필요 시 내부 스크롤)
                maxHeight: 'calc(100vh - 430px)',
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.9,
                  // 마지막 행 아래 여백을 모드와 무관하게 일정하게 확보
                  pb: 0.5,
                  overflowY: 'auto',
                  pr: 0.5,
                }}
              >
                {levels.map((row, idx) => {
                  const t = toneStyle(row.tone);
                  return (
                    <Box
                      key={`${row.label}-${idx}`}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.0,
                        px: 1.1,
                        py: 0.72,
                        borderRadius: 1.5,
                        bgcolor: t.bg,
                        border: `1px solid ${t.border}`,
                        boxShadow: '0 1px 0 rgba(15, 23, 42, 0.03)',
                      }}
                    >
                      <Box
                        sx={{
                          minWidth: 108,
                          height: 30,
                          borderRadius: 1,
                          bgcolor: t.chip,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 700,
                          color: '#1b1b1b',
                        }}
                      >
                        {row.label}
                      </Box>

                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <TextField
                          size="small"
                          value={row.min}
                          onChange={(e) =>
                            handleLevelScoreInput(idx, 'min', e.target.value)
                          }
                          inputProps={{
                            inputMode: 'numeric',
                            min: 0,
                            max: LEVEL_SCORE_MAX,
                          }}
                          sx={{ width: 72 }}
                        />
                        <Typography sx={{ color: 'text.secondary' }}>
                          ~
                        </Typography>
                        <TextField
                          size="small"
                          value={row.max}
                          onChange={(e) =>
                            handleLevelScoreInput(idx, 'max', e.target.value)
                          }
                          inputProps={{
                            inputMode: 'numeric',
                            min: 0,
                            max: LEVEL_SCORE_MAX,
                          }}
                          sx={{ width: 72 }}
                        />
                      </Box>

                      <Box sx={{ flex: 1 }} />

                      <FormControl size="small" sx={{ minWidth: 180 }}>
                        <Select
                          value={row.action}
                          onChange={(e) =>
                            handleLevelChange(idx, 'action', e.target.value)
                          }
                          sx={{
                            bgcolor: '#fff',
                            borderRadius: 1.25,
                            '& .MuiSelect-select': { py: 0.9 },
                          }}
                        >
                          {ACTION_ITEMS.map((v) => (
                            <MenuItem key={v} value={v}>
                              {v}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  );
                })}
              </Box>
            </Paper>
          </>
        ) : (
          <Typography variant="body2" color="text.secondary">
            평가방법이 &quot;위험성 수준 3단계판단법&quot;인 경우 평가척도 설정
            없이 진행합니다.
          </Typography>
        )}

        <Box
          sx={{
            display: 'flex',
            gap: 1,
            justifyContent: 'flex-end',
            mt: 2,
            pt: 1.5,
            borderTop: '1px solid rgba(0,0,0,0.06)',
          }}
        >
          <Button
            variant="outlined"
            color="inherit"
            onClick={onCancel}
            sx={{
              minWidth: 120,
              height: 40,
              borderRadius: 1.5,
              fontWeight: 800,
              color: '#334155',
              bgcolor: '#f8fafc',
              borderColor: '#cbd5e1',
              boxShadow: '0 1px 2px rgba(15, 23, 42, 0.06)',
              '&:hover': {
                borderColor: '#94a3b8',
                bgcolor: '#f1f5f9',
                boxShadow: '0 2px 6px rgba(15, 23, 42, 0.1)',
              },
            }}
          >
            취소
          </Button>
          <Button
            variant="contained"
            onClick={onSave}
            sx={{
              minWidth: 140,
              height: 40,
              borderRadius: 1.5,
              fontWeight: 900,
              letterSpacing: '0.01em',
              boxShadow: '0 8px 18px rgba(44, 169, 0, 0.3)',
              background:
                'linear-gradient(90deg, #217F00 0%, #2CA900 52%, #46B220 100%)',
              '&:hover': {
                boxShadow: '0 10px 22px rgba(44, 169, 0, 0.38)',
                background:
                  'linear-gradient(90deg, #1A6800 0%, #217F00 52%, #2CA900 100%)',
              },
            }}
          >
            저장
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}

export default Regulation;
